import { stopImpersonation } from '../redux/impersonation-slice';
import impersonationApiService from '../api/impersonation-api';
import { logoutSuccess } from '@/features/security/sagas/auth-slice';
import { sessionService } from '@/shared/services/session.service';

export interface VolverDeLaSuplantacionArgs {
  dispatch: (accion: any) => unknown;
  /** `POST /admin/impersonate/stop`. Inyectable para las pruebas. */
  detener?: () => Promise<unknown>;
  /** Navegación dura. Inyectable para las pruebas. */
  irA?: (ruta: string) => void;
}

/**
 * Lo que hace el botón «Volver a Super Admin» de la barra de MainLayout.
 *
 * DESTINO: `/admin/login`, siempre. Medido por CAC en producción (GH#2768, sha 95f11e33e): «Volver» terminaba en `/admin/login`. La causa está en
 * el INICIO de la suplantación: `ImpersonationFlyout.handleImpersonate` despacha `loginSuccess` para el suplantado, y `loginSuccess` hace
 * `sessionService.clearSession(true)` (auth-slice.ts, #1208: una sesión admin vieja contaminaba el guard y el token de un login normal).
 * Resultado: al entrar a la cuenta del cliente el slot del admin (cookie `admin_user_data` + `inbo_admin_*`) YA SE BORRÓ, y la copia que queda en
 * Redux (`impersonation.originalAdmin*`) muere con la recarga de `window.location.href = '/'`. No hay sesión de admin que restaurar, ni con la
 * página recargada ni sin recargar (la navegación de abajo también es una recarga). Ir a `/admin/dashboard` solo añadía un rebote por el guard.
 *
 * (Una versión anterior de este archivo, GH#2768 / PR #2810, decía lo contrario —«el slot del admin sigue en su sitio»— y mandaba al panel.
 * Estaba mal: su prueba sembraba a mano los dos slots, un estado que el inicio real de la suplantación nunca deja.)
 *
 * Arreglar de verdad la vuelta sin login (guardar la sesión del admin aparte, p. ej. en sessionStorage, y restaurarla aquí) toca el almacenamiento de
 * credenciales de un SUPER_ADMIN: queda como Issue aparte, con su OK.
 */
export async function volverDeLaSuplantacion({
  dispatch,
  detener = () => impersonationApiService.stop(),
  irA = (ruta) => {
    window.location.href = ruta;
  },
}: VolverDeLaSuplantacionArgs): Promise<void> {
  // TINA-RDIBACKEND (PR #1412, 82afaba3): clearSession del cliente no puede borrar la cookie HttpOnly auth_token que el servidor le puso al
  // target durante el START, y jwt.guard.ts la lee ANTES que el header — por eso el POST /admin/impersonate/stop va primero, best-effort
  // (si falla igual seguimos: el redirect no depende de esto).
  try {
    await detener();
  } catch (err) {
    console.error('No se pudo detener la impersonación del lado servidor', err);
  }

  // Se vacía el slot del suplantado (isAdmin=false: no toca el del admin, por si hubiera uno) y el estado de Redux, y se va al login del panel.
  sessionService.clearSession(false);
  dispatch(logoutSuccess());
  dispatch(stopImpersonation());
  irA('/admin/login');
}
